(function () {
  if (window.SonicReviewsIoSectionLoaded) return;
  window.SonicReviewsIoSectionLoaded = true;

  const sectionSelector = "[data-sonic-reviewsio-polaris]";
  const scriptUrl = "https://widget.reviews.co.uk/polaris/build.js";
  const disclosures = new WeakMap();

  function syncDisclosure(owner, selector, label, className) {
    const groups = Array.from(owner.querySelectorAll(selector)).filter(function (group) {
      return !group.closest(".sonic-review-details");
    });
    const populatedGroups = groups.filter(function (group) {
      return group.textContent.trim();
    });
    let state = disclosures.get(owner);

    if (!populatedGroups.length) {
      if (state) state.details.remove();
      groups.forEach(function (group) { group.classList.remove("sonic-review-detail-source"); });
      disclosures.delete(owner);
      return;
    }

    const signature = populatedGroups.map(function (group) { return group.innerHTML; }).join("\n");
    if (!state || !owner.contains(state.details)) {
      const details = document.createElement("details");
      details.className = `sonic-review-details ${className}`;
      if (className === "sonic-review-details--aggregate") details.setAttribute("open", "");
      const summary = document.createElement("summary");
      summary.textContent = label;
      const content = document.createElement("div");
      content.className = "sonic-review-details__content";
      details.append(summary, content);
      owner.appendChild(details);
      state = { details: details, content: content, signature: null };
      disclosures.set(owner, state);
    }

    groups.forEach(function (group) { group.classList.add("sonic-review-detail-source"); });
    if (signature === state.signature) return;
    state.signature = signature;
    state.content.replaceChildren();

    // Leave vendor-owned nodes in place so pagination, filters and Vue updates stay intact.
    populatedGroups.forEach(function (group) {
      const copy = group.cloneNode(true);
      copy.classList.remove("sonic-review-detail-source");
      copy.querySelectorAll("[id]").forEach(function (node) { node.removeAttribute("id"); });
      copy.removeAttribute("id");
      copy.querySelectorAll(".R-SliderIndicator-group").forEach(function (slider) {
        slider.removeAttribute("aria-hidden");
        const title = slider.querySelector(".R-TextHeading")?.textContent?.trim();
        const indicator = slider.querySelector(".R-SliderIndicator__button");
        const percent = Number.parseFloat(indicator?.style.left);
        if (title && Number.isFinite(percent)) {
          const description = document.createElement("p");
          description.className = "sonic-review-details__sr-only";
          description.textContent = (window.sonicStrings?.cleanliness_scale || "[title] [percent] out of 100 on the cleanliness scale.").replaceAll("[title]", () => title).replaceAll("[percent]", () => percent);
          slider.appendChild(description);
        }
      });
      state.content.appendChild(copy);
    });
  }

  function refinePresentation(target) {
    const header = target.querySelector(".header__inner");
    if (header) {
      syncDisclosure(header, ".header__group--center", (window.sonicStrings?.customer_survey || "Customer survey"), "sonic-review-details--aggregate");
    }
    target.querySelectorAll(".R-ContentList__item").forEach(function (review) {
      syncDisclosure(review, ".c-meta__authorAttributes, .c-item__attributesGroup, .c-item__slidersGroup",
        (window.sonicStrings?.customer_details || "Customer details"), "sonic-review-details--customer");
    });
  }

  function loadReviewsWidget() {
    if (window.ReviewsWidget) return Promise.resolve();
    if (window.SonicReviewsIoPolarisLoading) return window.SonicReviewsIoPolarisLoading;

    window.SonicReviewsIoPolarisLoading = new Promise(function (resolve, reject) {
      const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);

      if (existingScript) {
        existingScript.addEventListener("load", resolve, { once: true });
        existingScript.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.addEventListener("load", resolve, { once: true });
      script.addEventListener("error", reject, { once: true });
      document.head.appendChild(script);
    });

    return window.SonicReviewsIoPolarisLoading;
  }

  function syncSectionHeader(section, target) {
    const ratingNode = target.querySelector(".header__group--left .R-TextHeading");
    const rawRating = ratingNode?.textContent?.trim();
    const numericValue = Number(rawRating);
    const ratingValue = rawRating && Number.isFinite(numericValue)
      ? numericValue.toFixed(1).replace(/\.0$/, "")
      : rawRating;
    if (ratingNode && ratingNode.textContent.trim() !== ratingValue) ratingNode.textContent = ratingValue;
    const ratingCount = target.querySelector(".header__group--left .R-TextBody")?.textContent?.trim();

    if (ratingValue) {
      const numericRating = Number.parseFloat(ratingValue);

      section.querySelectorAll("[data-reviewsio-rating-value]").forEach(function (node) {
        node.textContent = ratingValue;
      });

      if (Number.isFinite(numericRating)) {
        section.querySelectorAll(".sonic-reviewsio__stars").forEach(function (node) {
          const ratingStars = node.querySelector(".wt-rating__value");
          if (ratingStars) {
            ratingStars.style.setProperty("--fill-pct", `${Math.max(0, Math.min(100, numericRating * 20))}%`);
          }
        });
        if (section.querySelector("[data-reviewsio-rating-value]")) {
          section.classList.add("sonic-reviewsio--summary-synced");
        }
      }
    }

    if (ratingCount) {
      const countText = ratingCount.replace(/Based\s+on\s+/i, "");
      section.querySelectorAll("[data-reviewsio-rating-count]").forEach(function (node) {
        node.textContent = countText;
      });
    }

    const mediaImages = Array.from(target.querySelectorAll(".R-MediaSlider__item img.image__src"))
      .map(function (image) {
        return image.currentSrc || image.src;
      })
      .filter(Boolean);

    section.querySelectorAll("[data-review-media-slot]").forEach(function (slot, index) {
      if (slot.querySelector("img") || !mediaImages[index]) return;

      const image = document.createElement("img");
      image.src = mediaImages[index];
      image.alt = "";
      image.loading = "lazy";
      slot.appendChild(image);
    });
  }

  function initialiseSection(section) {
    const configNode = section.querySelector("[data-sonic-reviewsio-config]");
    if (!configNode || section.dataset.reviewsioInitialised === "true") return;

    let config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      console.warn("Reviews.io config could not be parsed", error);
      return;
    }

    loadReviewsWidget()
      .then(function () {
        const target = section.querySelector(config.target);
        if (!window.ReviewsWidget || !target) return;

        section.dataset.reviewsioInitialised = "true";

        new window.ReviewsWidget(config.target, {
          store: config.store,
          widget: "polaris",
          options: {
            types: config.types,
            enable_sentiment_analysis: config.enableSentiment,
            lang: config.lang,
            layout: config.layout,
            per_page: config.perPage,
            store_review: { hide_if_no_results: false },
            third_party_review: { hide_if_no_results: false },
            product_review: {
              sku: config.productSku,
              hide_if_no_results: false,
              enable_rich_snippets: config.enableRichSnippets !== false,
              structured_data: config.enableRichSnippets === false ? undefined : config.structuredData,
            },
            questions: {
              hide_if_no_results: false,
              enable_ask_question: config.showAskQuestion,
              show_dates: true,
              grouping: config.questionGrouping,
            },
            header: {
              enable_summary: config.showHeader,
              enable_ratings: config.showHeader,
              enable_attributes: config.showHeader,
              enable_image_gallery: config.showImageGallery,
              enable_percent_recommended: false,
              enable_write_review: config.showWriteReview,
              enable_ask_question: config.showAskQuestion,
              enable_sub_header: config.showHeader,
              rating_decimal_places: 1,
              use_write_review_button: false,
              enable_if_no_results: false,
            },
            filtering: {
              enable: config.showFilters,
              enable_text_search: config.showFilters,
              enable_sorting: config.showFilters,
              enable_product_filter: false,
              enable_media_filter: config.showMediaFilter,
              enable_overall_rating_filter: config.showFilters,
              enable_language_filter: false,
              enable_language_filter_language_change: false,
              enable_ratings_filters: config.showFilters,
              enable_attributes_filters: config.showFilters,
            },
            reviews: {
              default_sort: "highest_rating",
              enable_avatar: true,
              enable_reviewer_name: true,
              enable_reviewer_address: true,
              reviewer_address_format: "city, country",
              enable_verified_badge: true,
              enable_subscriber_badge: true,
              review_content_filter: "undefined",
              enable_reviewer_recommends: true,
              enable_attributes: true,
              enable_product_name: true,
              enable_review_title: undefined,
              enable_replies: true,
              enable_images: true,
              enable_ratings: true,
              enable_share: true,
              enable_helpful_vote: true,
              enable_helpful_display: true,
              enable_report: true,
              enable_date: true,
              enable_third_party_source: true,
              enable_duplicate_reviews: undefined,
            },
          },
          translations: {
            "Verified Customer": config.verifiedCustomerText,
          },
          styles: config.styles,
        });

        const syncHeader = function () {
          syncSectionHeader(section, target);
          refinePresentation(target);
        };
        const observer = new MutationObserver(syncHeader);

        observer.observe(target, { childList: true, subtree: true });
        section.SonicReviewsIoObserver = observer;
        window.requestAnimationFrame(syncHeader);
        window.setTimeout(syncHeader, 800);
        window.setTimeout(syncHeader, 1800);
      })
      .catch(function (error) {
        console.warn("Reviews.io widget could not load", error);
      });
  }

  function initialiseAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const sections = scope.matches && scope.matches(sectionSelector) ? [scope] : scope.querySelectorAll(sectionSelector);
    sections.forEach(function (section) {
      if (section.dataset.reviewsioInitialised === "true" || section.SonicReviewsIoProximityObserver) return;
      if (!("IntersectionObserver" in window) || window.Shopify?.designMode) {
        initialiseSection(section);
        return;
      }
      const observer = new IntersectionObserver(function (entries) {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        initialiseSection(section);
      }, { rootMargin: "1000px 0px", threshold: 0 });
      section.SonicReviewsIoProximityObserver = observer;
      observer.observe(section);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initialiseAll(document);
    }, { once: true });
  } else {
    initialiseAll(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    initialiseAll(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    const section = event.target.matches(sectionSelector) ? event.target : event.target.querySelector(sectionSelector);
    section?.SonicReviewsIoProximityObserver?.disconnect();
    if (section?.SonicReviewsIoObserver) section.SonicReviewsIoObserver.disconnect();
  });
})();
